import { Abi, AbiFunction } from 'abitype/zod'
import {
  type AbiFunction as AbiFunctionType,
  decodeFunctionData,
  encodeFunctionData,
  toFunctionSelector,
} from 'viem'
import type { LowerHex } from '../../../types'
import type { TransactionFunction } from '../../../types/transactionDecoded'

type DecodeInputParameters = {
  input: LowerHex
  addressAbi?: string
  functionAbis: readonly string[]
}

export const decodeInput = ({
  input,
  addressAbi,
  functionAbis,
}: DecodeInputParameters): TransactionFunction | undefined => {
  // address ABI があれば、 address ABI を使用して　decode
  if (addressAbi !== undefined) {
    const decoded = decodeInputByAddressAbi(input, addressAbi)
    if (decoded !== undefined) {
      return decoded
    }
  }

  // address ABI で decode できない場合、 function ABI を使用して decode
  for (const functionAbi of functionAbis) {
    const decoded = decodeInputByFunctionAbi(input, functionAbi)
    if (decoded !== undefined) {
      return decoded
    }
  }

  // decode できない場合、 undefined を返す
  return undefined
}

const decodeInputByAddressAbi = (
  input: LowerHex,
  abiText: string,
): TransactionFunction | undefined => {
  try {
    const parsedAbi = Abi.safeParse(JSON.parse(abiText))
    if (!parsedAbi.success) {
      console.debug(parsedAbi.error)
      return undefined
    }
    const abi = parsedAbi.data
    const inputSelector = input.slice(0, 10)
    for (const abiItem of abi) {
      if (
        abiItem.type !== 'function' ||
        inputSelector !== toFunctionSelector(abiItem)
      ) {
        continue
      }
      const decoded = decodeInputByFunctionAbiItem(input, abiItem)
      if (decoded !== undefined) {
        return decoded
      }
    }
    return undefined
  } catch (error) {
    console.debug(error)
    return undefined
  }
}

const decodeInputByFunctionAbiItem = (
  input: LowerHex,
  abiItem: AbiFunctionType,
): TransactionFunction | undefined => {
  try {
    const { functionName, args } = decodeFunctionData({
      abi: [abiItem],
      data: input,
    })
    const functionArgs =
      args === undefined ? [] : createFunctionArgs(args, abiItem)
    return {
      args: functionArgs,
      name: functionName,
      interface: createInterface({ name: functionName, args: functionArgs }),
    }
  } catch (error) {
    console.debug(error)
    return undefined
  }
}

const decodeInputByFunctionAbi = (
  input: LowerHex,
  abiText: string,
): TransactionFunction | undefined => {
  try {
    const parsedAbi = AbiFunction.safeParse(JSON.parse(abiText))
    if (!parsedAbi.success) {
      console.debug(parsedAbi.error)
      return undefined
    }
    const abiItem = parsedAbi.data
    const { functionName, args } = decodeFunctionData({
      abi: [abiItem],
      data: input,
    })
    const data = encodeFunctionData({
      abi: [abiItem],
      functionName,
      args,
    })
    // input が data を含まない、または data.length <= input.length / 2 の場合は undefined を返す
    if (data.length <= input.length / 2 || !input.startsWith(data)) {
      return undefined
    }
    const functionArgs =
      args === undefined ? [] : createFunctionArgs(args, abiItem)
    return {
      args: functionArgs,
      name: functionName,
      interface: createInterface({ name: functionName, args: functionArgs }),
    }
  } catch (error) {
    console.debug(error)
    return undefined
  }
}

const createFunctionArgs = (
  args: readonly unknown[],
  abiItem: AbiFunctionType,
) =>
  args.map((value, index) => ({
    type: abiItem.inputs[index].type,
    name: abiItem.inputs[index].name ?? '',
    value,
  }))

const createInterface = ({
  name,
  args,
}: Pick<TransactionFunction, 'name' | 'args'>) => {
  return `${name}(${args.map(({ type }) => type).join(', ')})`
}
